import { omitBy, isNil } from 'lodash'
import brandKitApi from '@/api/brandKit'
import themeApi from '@/api/theme'
import Sentry, { featureTags } from '@/services/sentry'
import { injectCustomFont } from '@/utils/customFont'
import { normaliseThemeImages } from '@/utils/themeImage.utils'

export const KIT_SCOPE = {
  workspace: 'workspace',
  personal: 'personal',
}

const NO_BRAND_ONLY = { themes: false, colours: false, fonts: false }
const NO_PERMISSIONS = {
  isWorkspaceAdmin: false, canManageWorkspaceKit: false, canUseBrandKit: false, canUseBrandControls: false,
}

const initialState = () => ({
  kits: [],
  controls: null,
  brandOnly: { ...NO_BRAND_ONLY },
  permissions: { ...NO_PERMISSIONS },
  isLoaded: false,
  loadFailed: false,
})

const injectKitFonts = (kits) => {
  kits.forEach((kit) => {
    Object.values(kit.fonts || {}).forEach((font) => {
      if (font && font.source === 'custom') {
        injectCustomFont({ id: font.value, url: font.url, format: font.format })
      }
    })
  })
}

const toPickerTheme = (theme) => normaliseThemeImages(omitBy(theme, isNil))

const keptKits = (state, isLimited) => state.kits
  .filter((kit) => !isLimited || kit.scope === KIT_SCOPE.workspace)

let inFlightLoad = null

const getters = {
  kits: (state) => state.kits,
  isLoaded: (state) => state.isLoaded,
  loadFailed: (state) => state.loadFailed,
  kitById: (state) => (kitId) => state.kits.find((kit) => kit.id === kitId) || null,
  controls: (state) => state.controls,
  brandOnly: (state) => state.brandOnly,
  permissions: (state) => state.permissions,
  workspaceKit: (state) => state.kits.find((kit) => kit.scope === KIT_SCOPE.workspace) || null,
  personalKit: (state) => state.kits.find((kit) => kit.scope === KIT_SCOPE.personal) || null,
  isBrandOnlyThemes: (state) => Boolean(state.brandOnly.themes),
  isBrandOnlyColours: (state) => Boolean(state.brandOnly.colours),
  isBrandOnlyFonts: (state) => Boolean(state.brandOnly.fonts),
  // Personal-kit themes do not count as brand themes while the limit is on.
  pickerKits: (state, localGetters) => state.kits
    .filter((kit) => !localGetters.workspaceKit || kit.scope === KIT_SCOPE.workspace)
    .filter((kit) => (kit.themes || []).length > 0)
    .map((kit) => ({ ...kit, themes: kit.themes.map(toPickerTheme) })),
  brandThemes: (state, localGetters) => localGetters.pickerKits.flatMap((kit) => kit.themes),
  themeById: (state) => (themeId) => {
    const themes = state.kits.flatMap((kit) => kit.themes || [])
    const found = themes.find((theme) => theme.id === themeId)
    return found ? toPickerTheme(found) : null
  },
  brandColours: (state, localGetters) => {
    const seen = new Set()
    return keptKits(state, localGetters.isBrandOnlyColours)
      .flatMap((kit) => (kit.palettes || []).flatMap((palette) => palette.colors || []))
      .filter((colour) => {
        const key = String(colour.hex).toUpperCase()
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
  },
  // Admins and owners never get isRestricted: the server already folds them out of brandOnly.
  colourPickerOptions: (state, localGetters) => {
    const swatches = localGetters.brandColours.map((colour) => colour.hex)
    if (!swatches.length) return null
    return { swatches, isRestricted: localGetters.isBrandOnlyColours }
  },
  brandFonts: (state, localGetters) => keptKits(state, localGetters.isBrandOnlyFonts).flatMap((kit) => ['heading', 'body']
    .filter((role) => kit.fonts && kit.fonts[role])
    .map((role) => ({ ...kit.fonts[role], role }))),
}

/* eslint-disable no-param-reassign */
const mutations = {
  SET_BRAND_KITS(state, response) {
    state.kits = response.kits || []
    state.controls = response.controls || null
    state.brandOnly = { ...NO_BRAND_ONLY, ...(response.brandOnly || {}) }
    state.permissions = { ...NO_PERMISSIONS, ...(response.permissions || {}) }
    state.isLoaded = true
    state.loadFailed = false
  },
  SET_LOAD_FAILED(state) {
    state.loadFailed = true
  },
  RESET(state) {
    Object.assign(state, initialState())
  },
}
/* eslint-enable no-param-reassign */

const actions = {
  async fetchBrandKits({ commit, rootGetters }, { force = false } = {}) {
    if (!rootGetters['featureToggles/canUseBrandKit']) return
    if (inFlightLoad && !force) {
      await inFlightLoad
      return
    }
    inFlightLoad = (async () => {
      try {
        const response = await brandKitApi.list()
        injectKitFonts(response.kits || [])
        commit('SET_BRAND_KITS', response)
      } catch (error) {
        if (brandKitApi.isNotFound(error)) {
          commit('RESET')
        } else {
          Sentry.captureException(error, {
            tags: featureTags({ featureFlag: 'release-brand-kit', featureName: 'brand-kit-load', taskId: 'THEM-66' }),
          })
          Sentry.captureException(error, {
            tags: featureTags({ featureFlag: 'release-brand-kit-paywall', featureName: 'brand-kit-load', taskId: 'THEM-66' }),
          })
        }
        commit('SET_LOAD_FAILED')
      } finally {
        inFlightLoad = null
      }
    })()
    await inFlightLoad
  },
  async ensureLoaded({ dispatch, state, rootState }, { force = false } = {}) {
    if (state.isLoaded && !force) return
    if (rootState.isAuthenticated) {
      await dispatch('featureToggles/whenAuthFlagsReady', { timeoutMs: 10000 }, { root: true })
    }
    await dispatch('fetchBrandKits', { force })
  },
  async freshKit({ dispatch, getters: localGetters }, kitId) {
    await dispatch('fetchBrandKits', { force: true })
    return localGetters.kitById(kitId)
  },
  async createKit({ dispatch }, { scope, name }) {
    const { kit } = await brandKitApi.create({ scope, name })
    await dispatch('fetchBrandKits', { force: true })
    return kit
  },
  async updateKit({ dispatch }, { kitId, patch }) {
    const response = await brandKitApi.update(kitId, patch)
    await dispatch('fetchBrandKits', { force: true })
    return response
  },
  async saveControls({ dispatch }, controls) {
    await brandKitApi.saveControls(controls)
    await dispatch('fetchBrandKits', { force: true })
  },
  async createBrandTheme({ dispatch }, { kitId, theme }) {
    const created = await themeApi.createThemes({ ...theme, brandKitId: kitId })
    await dispatch('fetchBrandKits', { force: true })
    return created
  },
  async updateBrandTheme({ dispatch }, { id, payload }) {
    await themeApi.updateTheme(id, payload)
    await dispatch('fetchBrandKits', { force: true })
  },
  async deleteBrandTheme({ dispatch }, id) {
    await themeApi.deleteTheme(id)
    await dispatch('fetchBrandKits', { force: true })
  },
  async refreshPresentationOnOpen({ dispatch, getters: localGetters, rootState }) {
    try {
      await dispatch('ensureLoaded', { force: true })
      const attribute = rootState.presentationAttributeModule.presentationAttribute
      if (!attribute || !attribute.brandKitId) return undefined
      const theme = localGetters.themeById(attribute.themeId)
      if (!theme || (theme.brandRevision || 0) <= (attribute.brandRevision || 0)) return undefined
      await dispatch('applyBrandThemeSilently', { theme, presentationId: rootState.presentation.id })
      return theme
    } catch (error) {
      // A failed refresh leaves the deck on the theme values it already has.
      return undefined
    }
  },
  async applyBrandThemeSilently({ dispatch }, { theme, presentationId }) {
    await dispatch('updatePresentationAction', { fontFamily: theme.fontFamily }, { root: true })
    await dispatch('updatePresentationByIdAction', { id: presentationId, payload: { fontFamily: theme.fontFamily } }, { root: true })
    if (theme.logo) {
      await dispatch('updatePresentationAction', { logo: theme.logo, notShowAhaSlidesLogo: false }, { root: true })
      await dispatch('updatePresentationByIdAction', { id: presentationId, payload: { notShowAhaSlidesLogo: false } }, { root: true })
    }
    await dispatch('themeModule/setThemeAction', {
      presentationId,
      ...theme,
      themeId: theme.id,
    }, { root: true })
  },
}

export default {
  namespaced: true,
  state: initialState,
  getters,
  mutations,
  actions,
}
